import { useEffect, useRef, type KeyboardEvent, type ReactNode } from 'react'
import { Icon } from './icons/Icon'
import './FocusTrapDialog.css'

export interface FocusTrapDialogProps {
  titleId: string
  title: string
  onClose: () => void
  children: ReactNode
  footer?: ReactNode
  /** Element to focus first — defaults to the first focusable element found. */
  initialFocusRef?: React.RefObject<HTMLElement>
  width?: number
}

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'

/**
 * Generic focus-trapped modal dialog (`role="dialog"`, `aria-modal`) —
 * the reusable shell behind every "New X" / "Edit X" form in the product
 * (design-system.md §3.1 MandatoryCommentModal reuses this same contract).
 */
export function FocusTrapDialog({
  titleId,
  title,
  onClose,
  children,
  footer,
  initialFocusRef,
  width = 520,
}: FocusTrapDialogProps) {
  const dialogRef = useRef<HTMLDivElement>(null)
  const previouslyFocused = useRef<HTMLElement | null>(null)

  useEffect(() => {
    previouslyFocused.current = document.activeElement as HTMLElement | null
    const target =
      initialFocusRef?.current ??
      dialogRef.current?.querySelector<HTMLElement>(FOCUSABLE_SELECTOR) ??
      dialogRef.current
    target?.focus()
    return () => {
      previouslyFocused.current?.focus()
    }
    // Only run on mount/unmount — re-focusing on every render would fight the user.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  function handleKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    if (event.key === 'Escape') {
      event.stopPropagation()
      onClose()
      return
    }
    if (event.key !== 'Tab') return
    const node = dialogRef.current
    if (!node) return
    const focusable = Array.from(node.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(
      (el) => el.offsetParent !== null || el === document.activeElement,
    )
    if (focusable.length === 0) return
    const first = focusable[0]!
    const last = focusable[focusable.length - 1]!
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault()
      last.focus()
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault()
      first.focus()
    }
  }

  return (
    <div className="iw-modal-overlay" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div
        ref={dialogRef}
        className="iw-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        style={{ width }}
        onKeyDown={handleKeyDown}
      >
        <div className="iw-modal-header">
          <h2 id={titleId} className="iw-modal-title">
            {title}
          </h2>
          <button type="button" className="iw-icon-btn" onClick={onClose} aria-label="Close dialog">
            <Icon name="x" />
          </button>
        </div>
        <div className="iw-modal-body">{children}</div>
        {footer ? <div className="iw-modal-footer">{footer}</div> : null}
      </div>
    </div>
  )
}
